import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Flex } from 'reflexbox';

import { useErrorBoundary } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { GraphQLErrorMessage, Modal, Popover, Spinner } from '@treams/treams-ui';

import CreateConversationMomentInfo from './CreateConversationMomentInfo';
import { ConversationDateSelect } from './create-conversation/ConversationDateSelect';
import { ConversationTemplateSelect } from './create-conversation/ConversationTemplateSelect';

function CreateConversationModalWithMoment(props: {
	user: graphql.User;
	moment?: graphql.MomentInstance;
	returnURL: string;
}) {
	const { t } = useTranslation();
	const history = useHistory();

	const [create] = graphql.useCreateEvaluationSubmissionMutation({});

	const deadlineDefault = new Date();
	deadlineDefault.setDate(deadlineDefault.getDate() + 30);

	const [selected, setSelected] = useState<string | undefined>(
		props.moment?.momentTemplate.evaluation?.evaluationForm?.id,
	);
	const [deadline, setDeadline] = useState<Date | undefined>(deadlineDefault);
	const [meetingDuration, setMeetingDuration] = useState<number | undefined>(undefined);
	const [location, setLocation] = useState('');

	const [error, attempt] = useErrorBoundary();

	const onCreate = attempt(async () => {
		if (!selected) {
			return;
		}

		const { data } = await create({
			variables: {
				input: {
					userID: props.user.id,
					evaluationFormID: selected,
					deadline,
					momentID: props.moment?.id,
					meeting: meetingDuration
						? location === ''
							? { duration: meetingDuration }
							: { duration: meetingDuration, location }
						: undefined,
				},
			},
		});

		if (data?.createEvaluationSubmission.evaluationSubmission) {
			const url = `/users/${props.user.id}/conversations/${data.createEvaluationSubmission.evaluationSubmission.id}`;
			history.push(url);
		}
	});

	return (
		<Modal height="auto">
			<Modal.Header
				title={t('conversations.create.modal.title', { firstName: props.user.firstName })}
			/>

			<Modal.Contents>
				<GraphQLErrorMessage error={error} />
				{props.moment && <CreateConversationMomentInfo user={props.user} />}
				{!props.moment?.momentTemplate.evaluation?.evaluationForm && (
					<ConversationTemplateSelect
						userID={props.user.id}
						value={selected}
						onChange={setSelected}
					/>
				)}
				{selected && (
					<ConversationDateSelect
						value={{ deadline, meetingDuration, location }}
						onChange={({ deadline, meetingDuration, location }) => {
							setDeadline(deadline);
							setMeetingDuration(meetingDuration);
							setLocation(location);
						}}
					/>
				)}
			</Modal.Contents>

			<Modal.FormFooter
				onCancel={() => history.push(props.returnURL)}
				disabled={!selected}
				onSave={onCreate}
				saveTitle={t('evaluations.create.modal.submit')}
			/>
		</Modal>
	);
}

export default function CreateConversationModal(props: {
	userID: string;
	momentID?: string;
	returnURL: string;
}) {
	const user = graphql.useUserQuery({ variables: { id: props.userID } });
	const moment = graphql.useMomentForEvaluationQuery({
		variables: { id: props.momentID! },
		skip: !props.momentID,
	});

	return (
		<Popover visible center maxHeight="calc(100dvh - 40px)">
			{user.data && (moment.data || !props.momentID) ? (
				<CreateConversationModalWithMoment
					user={user.data.user as graphql.User}
					moment={moment.data?.momentInstance as graphql.MomentInstance}
					returnURL={props.returnURL}
				/>
			) : (
				<Modal>
					<Modal.Contents>
						<Flex alignItems="center" justifyContent="center" height={200}>
							<Spinner />
						</Flex>
					</Modal.Contents>
				</Modal>
			)}
		</Popover>
	);
}
